/**
 * Vehicle configuration / market entry intent. Plan §26.1.
 *
 * Kaiyang Lesson defenses encoded here:
 *   §25.3 — sample templates MUST have projectName ending in "(sample)"
 *           (superRefine).
 *   §25.4 — R5 enum fields are all `.nullable()` (NEVER `.default()`); UK route
 *           must be declared explicitly when UK is in selected markets
 *           (superRefine).
 *   §25.4 — Unknown applicability is field-level only — see `applicability.ts`.
 */

import { z } from "zod";

export const Powertrain = z.enum(["BEV", "PHEV", "HEV", "ICE", "FCEV"]).nullable();
export const AutomationLevel = z.enum(["L0", "L1", "L2", "L2+", "L3"]).nullable();
export const EuMarket = z.enum(["DE", "FR", "NL", "UK", "ES", "IT", "PL", "SE"]);
export const VehicleCategory = z.enum(["M1", "M2", "M3", "N1", "N2", "N3"]);
export const UkMarketRoute = z.enum(["GB", "NI", "GB+NI"]);
export const ApprovalAuthority = z.enum([
  "KBA",
  "UTAC",
  "RDW",
  "IDIADA",
  "VCA",
  "other",
]);

export const ApprovalType = z.enum(["new_type", "extension", "individual"]);
export const SteeringPosition = z.enum(["LHD", "RHD"]);
export const CompletionState = z.enum(["complete", "incomplete", "completed"]);
export const ConsumerOrFleet = z.enum(["consumer", "fleet"]);
export const SalesModel = z.enum(["dealer", "direct", "subscription"]);
export const ChargingOperatorRole = z.enum(["oem", "cpo", "msp", "none"]);
export const CommercialRole = z.enum([
  "manufacturer",
  "importer",
  "distributor",
  "dealer",
]);
export const SteeringAssistType = z.enum(["EPS", "HPS", "EHPS"]);

export const MarketEntryConfigSchema = z
  .object({
    projectName: z.string().nullable(),
    vehicleCategory: VehicleCategory.nullable(),
    steeringAssistType: SteeringAssistType.nullable().optional(),
    isSampleTemplate: z.boolean(),

    targetMarkets: z.object({
      eu_baseline: z.boolean(),
      selected_member_states: z.array(EuMarket),
    }),
    sopDate: z.string().nullable(),
    typeApprovalApplicationDate: z.string().nullable().optional(),
    firstRegistrationDate: z.string().nullable().optional(),

    powertrain: Powertrain,
    tractionBattery: z
      .object({
        capacityBand: z.enum(["small", "medium", "large"]),
      })
      .nullable(),
    batteryTechnical: z
      .object({
        exactCapacityKWh: z.number().nullable(),
        chemistry: z.string().nullable(),
      })
      .nullable()
      .optional(),

    automation: z
      .object({
        levelMax: AutomationLevel,
        fitsSilc: z.boolean(),
        laneChange: z.array(z.enum(["C1", "C2", "C3"])),
      })
      .nullable(),
    adasFeatureInventory: z
      .object({
        isa: z.boolean().nullable(),
        aeb: z.boolean().nullable(),
        elks: z.boolean().nullable(),
        addw: z.boolean().nullable(),
      })
      .nullable()
      .optional(),

    dms: z
      .object({
        fitted: z.boolean(),
        biometricCategorisation: z.boolean(),
        poseOnly: z.boolean(),
      })
      .nullable(),

    // AI / data (RFC-0006). `.nullable().optional()` matches the sibling
    // nullable sub-objects so existing configs without the key still parse.
    aiSystem: z
      .object({
        present: z.boolean(),
        level: z.enum(["none", "narrow", "foundation_model"]),
        safetyComponent: z.boolean(),
        dataFlags: z
          .array(z.enum(["cabin_camera", "driver_profiling", "emotion_recognition"]))
          .default(() => []),
      })
      .nullable()
      .optional(),
    ota: z
      .object({
        enabled: z.boolean().nullable(),
        functionalExtension: z.boolean(),
      })
      .nullable(),
    softwareUpdate: z
      .object({
        rxSwinMapAvailable: z.boolean().nullable(),
      })
      .nullable()
      .optional(),
    cybersecurity: z
      .object({
        relevantR155: z.boolean(),
      })
      .nullable(),
    connectivity: z
      .object({
        personalDataProcessing: z.boolean(),
        dataSharingThirdParty: z.boolean(),
      })
      .nullable(),
    charging: z
      .object({
        ac: z.boolean(),
        dc: z.boolean(),
        bidirectional: z.boolean(),
        homeChargingFr8A: z.boolean(),
      })
      .nullable(),
    hvSafety: z
      .object({
        r100EvidenceAvailable: z.boolean().nullable(),
      })
      .nullable()
      .optional(),

    reachesAftermarket: z.boolean(),

    approvalType: ApprovalType.nullable().optional(),
    steeringPosition: SteeringPosition.nullable().optional(),
    completionState: CompletionState.nullable().optional(),
    consumerOrFleet: ConsumerOrFleet.nullable().optional(),
    salesModel: SalesModel.nullable().optional(),
    parkingAutomation: z.boolean().nullable().optional(),
    chargingOperatorRole: ChargingOperatorRole.nullable().optional(),
    commercialRole: CommercialRole.nullable().optional(),

    ukMarketRoute: UkMarketRoute.nullable().optional(),
    approvalRoute: z
      .object({
        approvalAuthority: ApprovalAuthority.nullable(),
        technicalService: z.string().nullable(),
      })
      .nullable()
      .optional(),
  })
  .superRefine((cfg, ctx) => {
    // §25.3 — sample templates must carry the "(sample)" suffix.
    if (cfg.isSampleTemplate && !(cfg.projectName?.endsWith("(sample)") ?? false)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["projectName"],
        message: "Sample templates MUST have projectName ending in '(sample)'",
      });
    }
    // §25.4 — UK selected without explicit route declaration is rejected;
    // ukMarketRoute must be either an enum value or explicit `null` (unknown).
    if (
      cfg.targetMarkets.selected_member_states.includes("UK") &&
      cfg.ukMarketRoute === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ukMarketRoute"],
        message:
          "UK selected but ukMarketRoute is undefined; must explicitly be null (unknown) or one of GB/NI/GB+NI",
      });
    }
  });

export type MarketEntryConfig = z.infer<typeof MarketEntryConfigSchema>;
